import React, { useRef, useEffect, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import BusinessCard from "./common/BusinessCard";
import CustomerCard from "./common/CustomerCard";
import { createGSAPContext } from "../hooks/useGSAPCleanup";
import "./Section4Application.css";

gsap.registerPlugin(ScrollTrigger);

function Section4Application() {
  const sectionRef = useRef(null);
  const aliCardRef = useRef(null);
  const customer1Ref = useRef(null);
  const customer2Ref = useRef(null);
  const customer3Ref = useRef(null);
  const overlapZoneRef = useRef(null);

  const customerData = useMemo(
    () => [
      { name: "삼성", engName: "SAMSUNG", ceo: "이재용", color: "#1428A0" },
      { name: "LG", engName: "LG CORP", ceo: "구광모", color: "#A50034" },
      { name: "현대", engName: "HYUNDAI", ceo: "정의선", color: "#002C5F" },
    ],
    []
  );

  useEffect(() => {
    const section = sectionRef.current;
    const aliCard = aliCardRef.current;
    const customer1 = customer1Ref.current;
    const customer2 = customer2Ref.current;
    const customer3 = customer3Ref.current;

    if (!section) return;

    const ctx = createGSAPContext(() => {
      // Configuration constant for consistent x positioning
      const CUSTOMER_CARD_X_POSITION = -125;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top", // Pin when section reaches top
          end: "+=300%", // Extended scroll distance for better control
          scrub: 1,
          pin: true, // Pin the section for controlled storytelling
          anticipatePin: 1, // Smooth pin transition
          pinSpacing: true, // Maintain spacing
        },
      });

      // Initial setup - Ali card at center, customer cards off-screen top-left
      gsap.set(aliCard, {
        x: 0,
        z: 0,
        transformStyle: "preserve-3d",
        perspective: 1000,
      });
      gsap.set([customer1, customer2, customer3], {
        opacity: 0,
        x: -400,
        y: -500,
        z: 200,
        rotationX: -45,
        rotationY: 25,
        rotationZ: -15,
        transformOrigin: "center bottom",
        transformStyle: "preserve-3d",
      });
      gsap.set(".message", { opacity: 0 });
      gsap.set(overlapZoneRef.current, { opacity: 0 });

      // Show initial explanation
      tl.from(".example-subtitle", {
        opacity: 0,
        y: 20,
        duration: 0.5,
      })

        // Move Ali card to the right side
        .to(
          aliCard,
          {
            x: 200, // Move Ali card further right for better spacing
            duration: 0.8,
            ease: "power2.inOut",
          },
          "+=0.2"
        )
        // .to(overlapZoneRef.current, {
        //   opacity: 1,
        //   duration: 0.5,
        // })

        // Customer 1 - Hand picking and placing animation
        .to(customer1, {
          opacity: 1,
          x: -100,
          y: -200,
          z: 150,
          rotationX: -30,
          rotationY: 15,
          rotationZ: -10,
          duration: 0.8,
          ease: "power2.out",
        })
        // Card bending as if held
        .to(customer1, {
          x: 20,
          y: -50,
          z: 100,
          rotationX: -15,
          rotationY: 5,
          rotationZ: -5,
          scaleY: 0.98,
          boxShadow: "0 40px 80px rgba(0,0,0,0.4)",
          duration: 0.6,
          ease: "power2.inOut",
        })
        // Card releasing and settling
        .to(customer1, {
          x: CUSTOMER_CARD_X_POSITION,
          y: 0,
          z: 30,
          rotationX: -2,
          rotationY: 0,
          rotationZ: 0,
          scaleY: 1,
          duration: 0.5,
          ease: "back.out(1.5)",
        })
        // Final settle with bounce
        .to(customer1, {
          z: 20,
          rotationX: 0,
          boxShadow: "0 15px 40px rgba(0,0,0,0.3)",
          duration: 0.3,
          ease: "power2.out",
        })
        // .to(overlapZoneRef.current, {
        //   backgroundColor: customerData[0].color,
        //   borderColor: customerData[0].color,
        //   duration: 0.5,
        // })
        .to(".message-1", {
          opacity: 1,
          scale: 1.05,
          duration: 0.5,
        })
        // Fade out customer 1 and its message
        .to(
          [customer1, ".message-1"],
          {
            opacity: 0,
            y: -30,
            duration: 0.5,
          },
          "+=1"
        )

        // Customer 2 - Hand picking with more curve
        .to(customer2, {
          opacity: 1,
          x: -150,
          y: -250,
          z: 180,
          rotationX: -35,
          rotationY: 20,
          rotationZ: -12,
          duration: 0.8,
          ease: "power2.out",
        })
        // Card curving as held by corners
        .to(customer2, {
          x: 30,
          y: -60,
          z: 120,
          rotationX: -20,
          rotationY: 8,
          rotationZ: -3,
          scaleY: 0.96,
          scaleX: 0.99,
          boxShadow: "0 50px 100px rgba(0,0,0,0.45)",
          duration: 0.6,
          ease: "power2.inOut",
        })
        // Dropping and flattening
        .to(customer2, {
          x: CUSTOMER_CARD_X_POSITION + 10,
          y: 0,
          z: 40,
          rotationX: -3,
          rotationY: 1,
          rotationZ: 0.5,
          scaleY: 1,
          scaleX: 1,
          duration: 0.5,
          ease: "back.out(1.3)",
        })
        // Final placement
        .to(customer2, {
          x: CUSTOMER_CARD_X_POSITION,
          z: 35,
          rotationX: 0,
          rotationY: 0,
          rotationZ: 0,
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          duration: 0.3,
          ease: "power2.out",
        })
        .to(overlapZoneRef.current, {
          backgroundColor: customerData[1].color,
          borderColor: customerData[1].color,
          duration: 0.5,
        })
        .to(".message-2", {
          opacity: 1,
          scale: 1.05,
          duration: 0.5,
        })
        // Fade out customer 2 and its message
        .to(
          [customer2, ".message-2"],
          {
            opacity: 0,
            y: -30,
            duration: 0.5,
          },
          "+=1"
        )

        // Customer 3 - Most dramatic hand effect
        .to(customer3, {
          opacity: 1,
          x: -200,
          y: -300,
          z: 200,
          rotationX: -40,
          rotationY: 25,
          rotationZ: -15,
          duration: 0.8,
          ease: "power2.out",
        })
        // Strong bending like pinched at top
        .to(customer3, {
          x: 40,
          y: -80,
          z: 140,
          rotationX: -25,
          rotationY: 10,
          rotationZ: -2,
          scaleY: 0.94,
          scaleX: 0.98,
          boxShadow: "0 60px 120px rgba(0,0,0,0.5)",
          duration: 0.6,
          ease: "power2.inOut",
        })
        // Release with wobble
        .to(customer3, {
          x: CUSTOMER_CARD_X_POSITION + 10,
          y: 0,
          z: 50,
          rotationX: -5,
          rotationY: 2,
          rotationZ: 1,
          scaleY: 1.02,
          scaleX: 1,
          duration: 0.4,
          ease: "back.out(2)",
        })
        // Settling with final bounce
        .to(customer3, {
          x: CUSTOMER_CARD_X_POSITION,
          z: 45,
          rotationX: 0,
          rotationY: 0,
          rotationZ: 0,
          scaleY: 1,
          boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
          duration: 0.4,
          ease: "elastic.out(1, 0.5)",
        })
        .to(overlapZoneRef.current, {
          backgroundColor: customerData[2].color,
          borderColor: customerData[2].color,
          duration: 0.5,
        })
        .to(".message-3", {
          opacity: 1,
          scale: 1.05,
          duration: 0.5,
        })
        // Keep customer 3 visible for a moment
        .to(
          ".message-3",
          {
            scale: 1.1,
            duration: 0.3,
          },
          "+=0.5"
        )
        // Then fade out
        .to(
          [customer3, ".message-3"],
          {
            opacity: 0,
            y: -30,
            duration: 0.8,
            ease: "power2.out",
          },
          "+=0.5"
        );
    }, section);

    return () => {
      if (ctx && ctx.cleanup) {
        ctx.cleanup();
      }
    };
  }, [customerData]);

  return (
    <section className="section section-application" ref={sectionRef}>
      <div className="container">
        <h2 className="example-title">
          실제 활용 예시: 고객을 주인공으로
          <span className="mobile-notice" style={{
            display: 'none',
            fontSize: '0.5em',
            fontWeight: 'normal',
            marginTop: '0.5em',
            opacity: 0.7
          }}>
            (PC에서 최적화된 화면으로 보실 수 있습니다)
          </span>
        </h2>

        <div className="cards-stage">
          <div className="ali-card-wrapper" ref={aliCardRef}>
            <BusinessCard
              name="박대성"
              team="테크팀"
              phone="010-6533-1985"
              email="pds@alipeople.kr"
              showLogo={true}
              className="ali-card-demo"
            />
            <div className="overlap-zone-overlay" ref={overlapZoneRef}></div>
          </div>

          <div ref={customer1Ref} className="customer-card-wrapper customer-1">
            <CustomerCard
              companyName={customerData[0].name}
              engName={customerData[0].engName}
              ceoName={customerData[0].ceo}
              color={customerData[0].color}
            />
          </div>

          <div ref={customer2Ref} className="customer-card-wrapper customer-2">
            <CustomerCard
              companyName={customerData[1].name}
              engName={customerData[1].engName}
              ceoName={customerData[1].ceo}
              color={customerData[1].color}
            />
          </div>

          <div ref={customer3Ref} className="customer-card-wrapper customer-3">
            <CustomerCard
              companyName={customerData[2].name}
              engName={customerData[2].engName}
              ceoName={customerData[2].ceo}
              color={customerData[2].color}
            />
          </div>
        </div>

        <div className="messages">
          <p className="message message-1">
            {customerData[0].name}을 알리는 사람들
          </p>
          <p className="message message-2">
            {customerData[1].name}을 알리는 사람들
          </p>
          <p className="message message-3">
            {customerData[2].name}를 알리는 사람들
          </p>
        </div>
      </div>
    </section>
  );
}

export default Section4Application;
